/* tslint:disable */
// Angular imports
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

// 3rd party imports
import tinymce from 'tinymce/tinymce';
import { Observable } from 'rxjs';
import { SpellCheckerService } from 'ngx-spellchecker';

// CC-Common imports
import { CCLocalStorageCategory, CCLocalStorageService, Utils } from 'cc-common';

// Environment imports
import { environment } from '../../../environments/environment';

//service imports
import { UserService } from '../data-services/user.service';

// Models imports
import { TextValueInfo } from '../../_models/text-value-info';
import { KeyValueResultDto } from '../../_models/key-value-result-dto';
import { MergeFieldResultDto } from '../../_models/merge-field-result-dto';

@Injectable({
    providedIn: 'root'
})
export class HtmlEditorService {
    private readonly _storageKey = 'MP_LOCAL_DICTIONARY_WORDS';

    private readonly _localWordList: string[];
    private _dictionary: any;

    constructor(
        private readonly httpClient: HttpClient,
        private readonly localStorageService: CCLocalStorageService,
        private readonly spellChecker: SpellCheckerService,
        private readonly userService: UserService
    ) {
        window['editorService'] = this;
        this._localWordList = JSON.parse(
            this.localStorageService.getItem(this._storageKey, CCLocalStorageCategory.EMAIL) || '[]'
        );
        this.httpClient.get(`assets/dictionaries/en_US.txt`, { responseType: 'text' }).subscribe((result) => {
            this._dictionary = this.spellChecker.getDictionary(result.replace(/\r/gi, ''));
            this._dictionary.wordlist.push(...this._localWordList);
            this._dictionary.wordlist.sort();
        });
    }

    spellCheck(words: string[]): Observable<any> {
        return new Observable((observer) => {
            if (!!this._dictionary) {
                const suggestions = {};
                words.forEach((word) => {
                    const cs = this._dictionary.checkAndSuggest(word);
                    if (cs.misspelled && cs.suggestions.length > 0 && this._dictionary.wordlist.indexOf(word) === -1) {
                        suggestions[word] = cs.suggestions;
                    }
                });
                observer.next({ words: suggestions, dictionary: this._dictionary.wordlist });
            } else {
                observer.error('Spell checker failed!');
            }
        });
    }

    addToDictionary(word: string): void {
        if (!!this._dictionary && !!this._dictionary.wordlist && this._dictionary.wordlist.indexOf(word) === -1) {
            this._dictionary.wordlist.push(word);
            this._dictionary.wordlist.sort();
            this._localWordList.push(word);
            this.localStorageService.setItem(
                this._storageKey,
                JSON.stringify(this._localWordList),
                CCLocalStorageCategory.EMAIL
            );
        }
    }

    getEditorOptions(
        pageOptions: any,
        specialLinks: TextValueInfo[] = [],
        mergeTags: MergeFieldResultDto[] = [],
        signatures: KeyValueResultDto[] = [],
        editorToolbar: string = null,
        enableFullPagePlugin: boolean = false
    ): any {
        const toolbar = this.getEditorToolbar(editorToolbar);
        const elementOptions = !!enableFullPagePlugin
            ? { valid_elements: '+*[*]', invalid_elements: '', valid_children: '+head[style],+body[style]' }
            : {};
        return Object.assign(
            {
                base_url: environment.tinymceBaseUrl,
                suffix: '.min',
                min_height: 100,
                branding: false,
                elementpath: false,
                indent: false, //Temporary use only as it's undocument config and depricated
                plugins: `preview searchreplace autolink save fullscreen image table charmap hr pagebreak nonbreaking insertdatetime advlist lists imagetools noneditable help charmap quickbars emoticons spellchecker codesample code paste ${
                    enableFullPagePlugin ? 'fullpage' : ''
                }`,
                menubar: false,
                toolbar: toolbar,
                statusbar: false,
                quickbars_selection_toolbar: `bold italic | quicklink h1 h2 h3 blockquote quickimage quicktable | urlLink signatures speciallinks mergetags templates reset `,
                quickbars_insert_toolbar: false,
                toolbar_mode: 'wrap',
                lineheight_formats: '0.5 0.75 1 1.25 1.5 1.75 2',
                image_caption: true,
                default_link_target: '_blank',
                file_picker_types: 'image',
                autoresize_on_init: true,
                paste_data_images: true,
                paste_retain_style_properties: 'all',
                paste_webkit_styles: 'all',
                paste_tab_spaces: 1,
                paste_convert_word_fake_lists: false,
                paste_remove_styles_if_webkit: false,
                fix_list_elements: true,
                element_format: 'html',
                allow_html_in_named_anchor: true,
                remove_trailing_brs: false,
                forced_root_block: true,
                keep_styles: true,
                file_picker_callback(callback, value, meta) {
                    /* Provide image and alt text for the image dialog */
                    if (meta.filetype === 'image') {
                        const input = document.getElementById('imageInput');
                        input.onchange = (e: any) => {
                            if (e.target.files.length > 0) {
                                const file = e.target.files[0];
                                const reader = new FileReader();
                                reader.onload = function () {
                                    const id = `blobid-${new Date().getTime()}`;
                                    const base64 = reader.result.toString().split(',')[1];
                                    const blobCache = tinymce.activeEditor.editorUpload.blobCache;
                                    const blobInfo = blobCache.create(id, file, base64);
                                    blobCache.add(blobInfo);
                                    callback(blobInfo.blobUri(), { title: file.name, alt: file.name });
                                };
                                reader.readAsDataURL(file);
                            }
                        };
                        const click = document.createEvent('MouseEvents');
                        click.initEvent('click', true, false);
                        input.dispatchEvent(click);
                    }
                },
                spellchecker_languages: 'English=en',
                browser_spellcheck: true,
                contextmenu: true,
                spellchecker_callback(method, text, success, failure) {
                    const service: HtmlEditorService = window['editorService'];
                    if (method === 'spellcheck') {
                        const words = text.match(this.getWordCharPattern());
                        service.spellCheck(words).subscribe(
                            (result) => {
                                success(result);
                            },
                            (error) => {
                                failure(`Error during spellcheck - ${error}`);
                            }
                        );
                    } else if (method === 'addToDictionary') {
                        service.addToDictionary(text);
                        success();
                    } else {
                        failure('Unsupported spellcheck');
                    }
                },
                setup(editor) {
                    if (Utils.notNullOrEmpty(signatures)) {
                        editor.ui.registry.addIcon(
                            'cc-icon-signature',
                            '<i class="fas fa-signature cc-fontawesome-icons"></i>'
                        );
                        editor.ui.registry.addMenuButton('signatures', {
                            icon: 'cc-icon-signature',
                            tooltip: 'Signature',
                            fetch: (callback) => {
                                const items = signatures.map((sg) => {
                                    return {
                                        type: 'menuitem',
                                        text: sg.displayText,
                                        onAction: () => {
                                            editor.insertContent(sg.key);
                                        }
                                    };
                                });
                                callback(items);
                            }
                        });
                    }
                    if (Utils.notNullOrEmpty(specialLinks)) {
                        editor.ui.registry.addMenuButton('speciallinks', {
                            text: 'Special Links',
                            tooltip: 'Special Links',
                            fetch: (callback) => {
                                const items = specialLinks.map((sl) => {
                                    return {
                                        type: 'menuitem',
                                        text: sl.text,
                                        onAction: () => {
                                            editor.insertContent(sl.value);
                                        }
                                    };
                                });
                                callback(items);
                            }
                        });
                    }
                    if (Utils.notNullOrEmpty(mergeTags)) {
                        editor.ui.registry.addIcon(
                            'cc-icon-code-merge',
                            '<i class="fas fa-code-branch cc-fontawesome-icons"></i>'
                        );
                        editor.ui.registry.addMenuButton('mergetags', {
                            icon: 'cc-icon-code-merge',
                            tooltip: 'Merge Tags',
                            fetch: (callback) => {
                                const items = mergeTags.map((mt) => {
                                    return {
                                        type: 'menuitem',
                                        text: mt.title,
                                        code: mt.code,
                                        onAction: () => {
                                            let mergeValue = mt.value;
                                            if (!!pageOptions && pageOptions.hasOwnProperty('isNewTemplate')) {
                                                if (Utils.isTrue(pageOptions.isNewTemplate)) {
                                                    mergeValue = mt.code;
                                                }
                                            }
                                            mergeValue = mergeValue
                                                .replace(/<</g, '&lt;&lt;')
                                                .replace(/>>/g, '&gt;&gt;')
                                                .replace(/</g, '&lt;')
                                                .replace(/>/g, '&gt;');
                                            editor.insertContent(mergeValue);
                                        }
                                    };
                                });
                                callback(items);
                            }
                        });
                    }
                    if (Utils.notNullAndDefined(pageOptions)) {
                        if (pageOptions.hasOwnProperty('urlCallback')) {
                            editor.ui.registry.addIcon(
                                'cc-icon-url-link',
                                '<i class="fas fa-link cc-fontawesome-icons"></i>'
                            );
                            editor.ui.registry.addButton('urlLink', {
                                icon: 'link',
                                tooltip: 'Insert Link',
                                onAction: () => {
                                    const node = tinymce.activeEditor.selection.getContent();
                                    if (!!node) {
                                        const link = tinymce.activeEditor.selection.getNode();
                                        const linkContent = {
                                            href: link['href'],
                                            target: link['target'],
                                            text: link['text'],
                                            title: link['title']
                                        };
                                        if (!!linkContent.href) {
                                            pageOptions.urlCallback(editor, linkContent);
                                        } else {
                                            pageOptions.urlCallback(editor);
                                        }
                                    } else {
                                        pageOptions.urlCallback(editor);
                                    }
                                }
                            });
                        }
                        if (pageOptions.hasOwnProperty('manageTemplateCallback')) {
                            editor.ui.registry.addIcon(
                                'cc-icon-templates',
                                '<i class="fas fa-swatchbook cc-fontawesome-icons"></i>'
                            );
                            editor.ui.registry.addButton('templates', {
                                icon: 'cc-icon-templates',
                                tooltip: 'Manage Template',
                                onAction: () => {
                                    pageOptions.manageTemplateCallback(editor);
                                }
                            });
                        }
                        if (pageOptions.hasOwnProperty('saveTemplateCallback')) {
                            editor.ui.registry.addIcon(
                                'cc-icon-save',
                                '<i class="fas fa-save cc-fontawesome-icons"></i>'
                            );
                            editor.ui.registry.addButton('saveTemplate', {
                                icon: 'cc-icon-save',
                                tooltip: 'Save Template',
                                onAction: () => {
                                    pageOptions.saveTemplateCallback();
                                }
                            });
                        }
                        if (pageOptions.hasOwnProperty('resetEditorCallback')) {
                            editor.ui.registry.addIcon(
                                'cc-icon-reset',
                                '<i class="fas fa-undo-alt cc-fontawesome-icons"></i>'
                            );
                            editor.ui.registry.addButton('reset', {
                                icon: 'cc-icon-reset',
                                tooltip: 'Reset Content',
                                onAction: () => {
                                    pageOptions.resetEditorCallback(editor);
                                }
                            });
                        }
                        if (pageOptions.hasOwnProperty('content') && Utils.notNullOrEmpty(pageOptions.content)) {
                            editor.setContent(pageOptions.content);
                        }
                        if (pageOptions.hasOwnProperty('setupAIEditor')) {
                            pageOptions.setupAIEditor(editor);
                        }
                        if (pageOptions.hasOwnProperty('onContentChanged')) {
                            editor.on('Change', function (event) {
                                pageOptions.onContentChanged(event);
                            });
                        }
                        if (pageOptions.hasOwnProperty('readonly')) {
                            editor.on('init', function () {
                                const isReadOnly = pageOptions.readonly === 1;
                                editor.mode.set(isReadOnly ? 'readonly' : 'design');
                                editor.getBody().setAttribute('contenteditable', String(!isReadOnly));
                            });
                        }
                        // Setup custom fullscreen toggle button
                        editor.ui.registry.addIcon(
                            'cc-icon-expand',
                            '<i class="fas fa-expand cc-fontawesome-icons"></i>'
                        );
                        editor.ui.registry.addButton('expand', {
                            icon: 'cc-icon-expand',
                            tooltip: 'Toggle Fullscreen',
                            onAction: () => {
                                editor.execCommand('mceFullscreen');
                            }
                        });
                        editor.on('FullscreenStateChanged', function (event) {
                            const buttonSpan = event.target.container.querySelector(
                                'button[title="Toggle Fullscreen"] span'
                            );
                            if (Utils.notNullAndDefined(buttonSpan)) {
                                if (Utils.isTrue(event.state)) {
                                    buttonSpan.innerHTML = '<i class="cc-fontawesome-icons fas fa-compress"></i>';
                                } else {
                                    buttonSpan.innerHTML = '<i class="cc-fontawesome-icons fas fa-expand"></i>';
                                }
                            }
                            if (pageOptions.hasOwnProperty('onFullScreenStateChanged')) {
                                pageOptions.onFullScreenStateChanged(event);
                            }
                        });
                    }
                },
                content_style:
                    'body { font-family:Tahoma,Helvetica,Arial,sans-serif; font-size:10pt; line-height:1.25 }'
            },
            elementOptions,
            pageOptions
        );
    }

    private getEditorToolbar(toolbar: string): string {
        let toolbarOptions: string;
        if (Utils.notNullOrEmpty(toolbar)) {
            toolbarOptions = toolbar;
        } else {
            const isITUser = this.userService.isITUser();
            if (Utils.isTrue(isITUser)) {
                toolbarOptions = `undo redo | 
                    bold italic underline strikethrough superscript subscript | 
                    fontselect fontsizeselect formatselect | 
                    alignleft aligncenter alignright alignjustify lineheight | 
                    forecolor backcolor removeformat | 
                    outdent indent numlist bullist | 
                    image urlLink hr charmap insertdatetime | 
                    searchreplace expand code preview help | 
                    signatures speciallinks mergetags reset`;
            } else {
                toolbarOptions = `undo redo | 
                    bold italic underline strikethrough superscript subscript | 
                    fontselect fontsizeselect formatselect | 
                    alignleft aligncenter alignright alignjustify lineheight | 
                    forecolor backcolor removeformat | 
                    outdent indent numlist bullist | 
                    image urlLink hr charmap insertdatetime | 
                    searchreplace expand preview help | 
                    signatures speciallinks mergetags reset`;
            }
        }

        return toolbarOptions;
    }
}
